import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetNptRollupQuery } from "@api/pApi";
import { useGetResolvedCatalogQuery } from "@api/mApi";
import { fmtHours, fmtNum, fmtPct } from "@lib/format";

/**
 * Time log / NPT — the non-productive-time roll-up by cause (server-computed
 * by Oxbo-P via /npt-rollup) plus the activity-code reference from Oxbo-M's
 * resolved catalog. The FE only displays the server's roll-up; it never sums
 * or classifies NPT itself.
 */
export function NptPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetNptRollupQuery(eventId, {
    skip: !eventId,
  });
  const { data: activityCat } = useGetResolvedCatalogQuery({ categoryKey: "activity_codes" });

  const stats: Stat[] = data
    ? [
        { label: t("npt.kpi.totalHours"), value: fmtHours(data.total_hours, locale) },
        { label: t("npt.kpi.nptHours"), value: fmtHours(data.npt_hours, locale), tone: "warn" },
        {
          label: t("npt.kpi.nptPct"),
          value: fmtPct(data.npt_pct, locale, 1),
          tone: data.npt_pct != null && data.npt_pct > 10 ? "crit" : "warn",
        },
      ]
    : [];

  const rows = data?.by_class ?? [];
  const denom = data?.npt_hours || rows.reduce((s, r) => s + r.total, 0);

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && rows.length === 0}
      emptyMessage={t("npt.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("npt.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <h3>{t("npt.byCause")}</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("npt.col.cause")}</th>
                  <th className="num">{t("npt.col.hours")}</th>
                  <th className="num">{t("npt.col.share")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const share = denom ? (r.total / denom) * 100 : 0;
                  return (
                    <tr key={r.key} data-owner="p">
                      <td>{r.key}</td>
                      <td className="num tab-nums">{fmtNum(r.total, locale, { maximumFractionDigits: 1 })}</td>
                      <td className="num">
                        <div className="mini-bar">
                          <span style={{ width: `${Math.min(100, share)}%` }} />
                        </div>
                        <span className="mono" style={{ fontSize: 11 }}>
                          {fmtPct(share, locale)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3>{t("npt.activityCodes")}</h3>
          <div className="chip-list" data-owner="m">
            {(activityCat?.entries ?? []).map((e) => (
              <span key={e.id} className="code-chip" title={e.display_label}>
                <b>{e.entry_key}</b> {e.display_label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </QueryState>
  );
}
